/*
eslint-disable @typescript-eslint/no-explicit-any,
import/no-unresolved,
@typescript-eslint/no-unused-vars,
max-len,
no-case-declarations
*/
import React from 'react';
import { BlockTool } from '@editorjs/editorjs/types/tools/block-tool';
import { API, ToolConfig, BlockToolData } from '@editorjs/editorjs';
import uuid from 'uuid';
// import { icon } from '@fortawesome/fontawesome-svg-core';
// import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

import {
  InstructionCSSClasses, InstructionAttribute, InstructionEvent, BaseSaveInstructionData,
} from '../types';
import { detectIOS } from '../../../../utility/helpers';
import { addInstructionEvent, renderExclamationIcon } from '../utils';
import BlockSettings from '../block-settings/blockSettings';

type ConstructorParams = {
  api: API;
  config: ToolConfig;
  data: BlockToolData;
};

type InstructionBlockData = BlockToolData & {
  instructionId?: string;
};

type InstructionClasses = {
  wrapper: InstructionCSSClasses;
  content: InstructionCSSClasses;
  action: InstructionCSSClasses;
};

type Toolbox = {
  title: string;
  icon: string;
};

abstract class BlockInstruction implements BlockTool {
  abstract get instructionClasses(): InstructionClasses;

  abstract openModal(instructionId: string): void;

  abstract initEvents(): void;

  abstract removeEvents(): void;

  abstract get isInit(): boolean;

  abstract set isInit(value: boolean);

  static get toolbox(): Toolbox {
    return {
      icon: '',
      title: '',
    };
  }

  abstract get tooltip(): string;

  protected _data: InstructionBlockData;

  protected _element: HTMLDivElement;

  protected readonly api: API;

  protected currentBlockIndex: number = 0;

  constructor({ data, api }: ConstructorParams) {
    this.api = api;
    this._data = {};
    this._element = this.renderInstructionElement();
    this.data = data;
    // @ts-ignore
    this.isInit = true;
    this.cancelInstruction = this.cancelInstruction.bind(this);
    this.removeInstruction = this.removeInstruction.bind(this);
    this.removeInstructionWithoutSave = this.removeInstructionWithoutSave.bind(this);
  }


  protected renderInstructionElement(): HTMLDivElement {
    const wrapper = document.createElement<'div'>('div');
    const action = document.createElement<'span'>('span');
    const content = document.createElement<'div'>('div');

    wrapper.classList.add(
      InstructionCSSClasses.BLOCK_INSTRUCTION_WRAPPER,
      this.instructionClasses.wrapper,
    );
    action.classList.add(
      InstructionCSSClasses.BLOCK_INSTRUCTION_ACTION,
      this.instructionClasses.action,
    );

    action.setAttribute(InstructionAttribute.tooltip, this.getTooltip(this._data.tooltip || ''));
    renderExclamationIcon(action as HTMLElement, this._data.instructionId);

    content.classList.add(
      InstructionCSSClasses.BLOCK_INSTRUCTION_CONTENT,
      this.instructionClasses.content,
    );
    wrapper.contentEditable = 'false';
    if (detectIOS()) {
      action.onmouseenter = this.actionHandler;
    }
    action.onclick = this.actionHandler;

    wrapper.appendChild(action);
    wrapper.appendChild(content);

    return wrapper;
  }

  actionHandler = (event: MouseEvent | TouchEvent) => {
    const mappedTarget = event.target as HTMLSpanElement;
    const { parentElement } = mappedTarget;
    if (parentElement) {
      const instructionId = parentElement.getAttribute(InstructionAttribute.instructionId);
      if (instructionId) {
        this.currentBlockIndex = this.api.blocks.getCurrentBlockIndex();
        this.openModal(instructionId);
        this.baseInitEvents();
      }
    }
  };

  protected get data() {
    return this._data;
  }

  protected set data(data: InstructionBlockData) {
    // @ts-ignore
    this._data = { ...this._data, ...data };

    const contentElement = this._element.querySelector(`.${this.instructionClasses.content}`);
    if (contentElement) {
      // @ts-ignore
      contentElement.innerHTML = this.data.text || '';
    }
    if (this.data.instructionData) {
      this._element.setAttribute(InstructionAttribute.data, this.data.instructionData);
    }

    const actionElement = this._element.querySelector(`.${this.instructionClasses.action}`);
    if (actionElement) {
      actionElement.setAttribute(InstructionAttribute.tooltip, this.getTooltip(this.data.tooltip || ''));
      renderExclamationIcon(actionElement as HTMLElement, this._data.instructionId);
    }

    if (this._data.instructionId) {
      this._element.setAttribute(InstructionAttribute.instructionId, this._data.instructionId);
    } else if (this.isInit) {
      this._data.instructionId = uuid();
      this._element.setAttribute(InstructionAttribute.instructionId, this._data.instructionId);
      this.api.events.emit(InstructionEvent.SET_PREVENT_ON_CHANGE_EDITOR, true);
      setTimeout(() => {
        this.currentBlockIndex = this.api.blocks.getCurrentBlockIndex();
        this.openModal(this._data.instructionId || '');
        this.baseInitEvents();
      }, 100);
    }
  }

  merge(data: BlockToolData) {
    const newData = {
      // @ts-ignore
      text: this.data.text + data.text,
    };

    this.data = newData;
  }

  render(): HTMLElement {
    return this._element;
  }


  save(block: HTMLElement): BlockToolData {
    try {
      const contentElement = block.querySelector(`.${this.instructionClasses.content}`);
      const actionElement = block.querySelector(`.${this.instructionClasses.action}`);
      const tooltipText = actionElement?.getAttribute(InstructionAttribute.tooltip);

      return {
        text: contentElement?.innerHTML || '',
        instructionId: block.getAttribute(InstructionAttribute.instructionId) || uuid(),
        data: block.getAttribute(InstructionAttribute.data) || '',
        tooltip: this.getTooltip(tooltipText || ''),
      };
    } catch (e) {
      return { text: '' };
    }
  }

  validate(savedData: BlockToolData) {
    return true;
  }

  static get sanitize() {
    return {
      div: true,
    };
  }

  protected saveInstruction(data: BaseSaveInstructionData) {
    // @ts-ignore
    this.data = data;
    this.baseRemoveEvents();
    const timer = setTimeout(() => {
      this.api.events.emit(InstructionEvent.SUBMIT_DATA, {});
      clearTimeout(timer);
    }, 0);
  }

  protected cancelInstruction() {
    this.baseRemoveEvents();
  }

  protected baseInitEvents() {
    this.initEvents();

    addInstructionEvent(
      this.api.events,
      InstructionEvent.CANCEL_INSTRUCTION,
      this.cancelInstruction,
    );
    addInstructionEvent(
      this.api.events,
      InstructionEvent.REMOVE_INSTRUCTION_BLOCK,
      this.removeInstruction,
    );

    addInstructionEvent(
      this.api.events,
      InstructionEvent.REMOVE_INSTRUCTION_BLOCK_WITHOUT_SAVING,
      this.removeInstructionWithoutSave,
    );
  }

  protected baseRemoveEvents() {
    this.removeEvents();
    this.api.events.off(
      InstructionEvent.CANCEL_INSTRUCTION,
      this.cancelInstruction,
    );
    this.api.events.off(
      InstructionEvent.REMOVE_INSTRUCTION_BLOCK,
      this.removeInstruction,
    );
    this.api.events.off(
      InstructionEvent.REMOVE_INSTRUCTION_BLOCK_WITHOUT_SAVING,
      this.removeInstructionWithoutSave,
    );
  }

  protected removeInstructionWithoutSave() {
    try { this.api.blocks.delete(this.currentBlockIndex); } catch (e) {}
    this.api.caret.setToBlock(Math.max(this.currentBlockIndex - 1, 0), 'end');
    this.baseRemoveEvents();
  }

  protected removeInstruction() {
    this.removeInstructionWithoutSave();
    const timer = setTimeout(() => {
      this.api.events.emit(InstructionEvent.SUBMIT_DATA, {});
      clearTimeout(timer);
    }, 0);
  }

  protected getTooltip(text: string) {
    return text ? `${this.tooltip}: ${text.replace(`${this.tooltip}:`, '')}`.trim() : `${this.tooltip}:`;
  }

  renderSettings() {
    const blockSettings = new BlockSettings({ api: this.api });

    return blockSettings.render();
  }
}

export default BlockInstruction;
